import type { GridColDef, GridValidRowModel } from "@mui/x-data-grid"

const CSV_DELIMITER = ","
// The cells a spreadsheet would run as a formula. See https://owasp.org/www-community/attacks/CSV_Injection
const FORMULA_PREFIXES = ["=", "+", "-", "@", "\t", "\r"]

// Quoted and escaped as the grid's own export does.
const sanitizeCsvValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ""
  }
  const text = String(value)
  const escaped = text.replace(/"/g, '""')
  if (FORMULA_PREFIXES.includes(escaped[0])) {
    return `"'${escaped}"`
  }
  return [CSV_DELIMITER, "\n", "\r", '"'].some((char) => text.includes(char))
    ? `"${escaped}"`
    : escaped
}

// The grid's CSV for rows it does not hold: same headers, formatted values and escaping.
export const buildGridCsv = <R extends GridValidRowModel>(
  rows: R[],
  columns: GridColDef<R>[],
): string => {
  const exported = columns.filter((column) => !column.disableExport)
  const header = exported
    .map((column) => sanitizeCsvValue(column.headerName ?? column.field))
    .join(CSV_DELIMITER)
  const body = rows.map((row) =>
    exported
      .map((column) => {
        const value = row[column.field]
        // Only the value is passed in, and no valueGetter runs: none of the exported
        // columns reads the grid's api or defines a getter.
        const formatted = column.valueFormatter
          ? column.valueFormatter(value as never, row, column, undefined as never)
          : value
        return sanitizeCsvValue(formatted)
      })
      .join(CSV_DELIMITER),
  )
  return [header, ...body].join("\r\n")
}

export const saveFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  link.setAttribute("class", "hidden")
  document.body.appendChild(link)
  link.click()
  link.parentNode?.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// With a BOM, as the tables export it: Excel reads the file as UTF-8 only then.
export const saveCsv = (csv: string, filename: string) =>
  saveFile(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }), filename)
